import type { Spacing } from '@frontend/constants';
import { match } from '@frontend/duck-tape';
import type { ReactNode, UIEventHandler } from '@frontend/react';
import { useContext } from '@frontend/react';
import type { ClassName } from '@frontend/web-utils';
import { concatClasses, safeCss } from '@frontend/web-utils';
import { MobileLayoutContext } from '../../../context/MobileLayoutContext';
import { Header, ScrollView } from '../../index';
import { Spinner } from '../Spinner';
import { YStack } from './Stacks/YStack';
import type { StackProps } from './Stacks/helpers';

export type PageContainerProps = StackProps & {
  HeaderComponent?: ReactNode;
  LoadingComponent?: ReactNode;
  contentContainerClassName?: ClassName;
  countSkeletons?: number;
  disableInternalScroll?: boolean;
  headerTitle?: string;
  innerContainerClassName?: ClassName;
  isFlexibleHeight?: boolean;
  isLoading?: boolean;
  isScrollable?: boolean;
  onScroll?: UIEventHandler<HTMLDivElement>;
  padding?: Spacing;
};
// When we eventually make a FlatList for web component we can remove the UI/logic related to isLoading
export const PageContainer = ({
  className,
  contentContainerClassName,
  disableInternalScroll: propDisableInternalScroll,
  isLoading = false,
  padding = 'lg',
  children,
  isFlexibleHeight,
  headerTitle,
  innerContainerClassName,
  isScrollable = true,
  LoadingComponent = (
    <YStack alignItems="center" justifyContent="center" style={{ flex: 1 }}>
      <Spinner color="neutral.9" size="lg" />
    </YStack>
  ),
  HeaderComponent = headerTitle ? <Header className="pl-sm" title={headerTitle} titleSize="h4" /> : null,
  onScroll,
  ...props
}: PageContainerProps) => {
  const paddingClassName = getPaddingClassName(padding);
  const { disableInternalScroll: contextDisableInternalScroll } = useContext(MobileLayoutContext);
  const disableInternalScroll = propDisableInternalScroll ?? contextDisableInternalScroll;
  const shouldScroll = isScrollable && !disableInternalScroll;

  return (
    <YStack
      className={concatClasses(
        disableInternalScroll ? 'flex-1 bg-cream-50' : 'size-full flex-1 overflow-clip bg-cream-50',
        className,
      )}
      {...props}
    >
      {HeaderComponent}
      <YStack
        className={concatClasses(
          'flex-1',
          disableInternalScroll ? '' : 'size-full',
          isLoading && padding === 'none' ? 'px-lg' : paddingClassName,
          disableInternalScroll || isFlexibleHeight ? '' : 'max-h-screen',
          innerContainerClassName,
        )}
        {...props}
      >
        {isLoading ? (
          LoadingComponent
        ) : shouldScroll ? (
          <ScrollView className={contentContainerClassName} onScroll={onScroll}>
            {children}
          </ScrollView>
        ) : (
          children
        )}
      </YStack>
    </YStack>
  );
};

const getPaddingClassName = (padding: Spacing) =>
  match(padding)
    .with('none', () => safeCss('p-none'))
    .with('xs', () => safeCss('px-xs'))
    .with('sm', () => safeCss('px-sm'))
    .with('md', () => safeCss('px-md'))
    .with('lg', () => safeCss('px-lg'))
    .with('xl', () => safeCss('px-xl'))
    .with('xxl', () => safeCss('px-xxl'))
    .with('xxxl', () => safeCss('px-xxxl'))
    .with('1', () => safeCss('px-1'))
    .exhaustive();
